import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { Component, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FastCheckinSessionService } from './fast-checkin-session.service';
import { CalculateParkingExtensionPriceResultDTO, FastCheckinParkingSessionInfoDTO } from 'src/app/base/models/models.swagger.generated';
import { TranslateModule } from '@ngx-translate/core';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { FastCheckinSessionVisitorActivationComponent } from './fast-checkin-session-visitor-activation/fast-checkin-session-visitor-activation.component';
import { PriceUtils } from 'src/app/base/utils/price-utils';
import { FastCheckinSessionClosedComponent } from './fast-checkin-session-closed/fast-checkin-session-closed.component';
import { FastCheckinSessionActiveComponent } from './fast-checkin-session-active/fast-checkin-session-active.component';
import { MobileHeaderComponent } from '../mobile-landing/shared/mobile-header/mobile-header.component';

@Component({
  selector: 'app-fast-checkin-session',
  templateUrl: './fast-checkin-session.component.html',
  styleUrls: ['./fast-checkin-session.component.scss'],
  imports: [TranslateModule, FastCheckinSessionVisitorActivationComponent, FastCheckinSessionClosedComponent, FastCheckinSessionActiveComponent, MobileHeaderComponent]
})
export class FastCheckinSessionComponent implements OnInit {
  public showError: boolean = false;
  public errorMessage: string = '';
  public showSuccess: boolean = false;
  public sessionInfo: FastCheckinParkingSessionInfoDTO | null = null;
  public extensionPriceWithCurrency: string = '';
  public isExtensionPriceLoading: boolean = false;

  public get title(): string | null {
    if (this.sessionInfo) {
      if (this.sessionInfo.isClosed) {
        return null;
      } else {
        return 'i18n.domain.fast-checkin-session.active-session-title'
      }
    }
    return '';
  }

  public get sessionPriceWithCurrency(): string {
    if (this.sessionInfo) {
      return PriceUtils.getPriceWithCurrency(this.sessionInfo.totalWithVat, this.sessionInfo.currency);
    }
    return '';
  }

  private sessionIdent: string | null = null;
  private isValidatingParkingCard: boolean = false;
  private cachedExtensionDurationMinutes: number | null = null;
  private cachedExtensionCalculation: CalculateParkingExtensionPriceResultDTO | null = null;

  constructor(private renderer: Renderer2,
    private route: ActivatedRoute,
    private service: FastCheckinSessionService,
    private spinnerService: SpinnerService,
    private modalService: ModalService) { }

  ngOnInit() {
    this.renderer.addClass(document.body, 'body-login');

    this.route.paramMap.subscribe(params => {
      const sessionIdent = params.get('ident');
      if (sessionIdent && this.sessionIdent !== sessionIdent) {
        this.sessionIdent = sessionIdent;
        this.loadFastCheckinParkingSessionInfo();
      }
    });
  }

  public onDownloadReceipt(): void {
    this.spinnerService.show();
    this.service.downloadReceipt(this.sessionIdent).subscribe({next: (fileDto) => {
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${fileDto.contentBase64}`;
      link.download = fileDto.reportName!;
      link.click();
      this.spinnerService.hide();
    }, error: err => {
      this.spinnerService.hide();
    }});
  }

  public onCheckOut() {
    const dialog = this.modalService.showOkCancelDialog('i18n.domain.fast-checkin-session.close-session', 'i18n.domain.fast-checkin-session.close-session-dialog-content', true);
    dialog.onOkSubject.subscribe(() => {
      this.spinnerService.show();
      this.service.checkOutFromFastCheckinSession(this.sessionIdent!).subscribe({next: sessionInfo => {
          this.sessionInfo = sessionInfo;
          this.showSuccess = true;
          this.showError = false;
          this.spinnerService.hide();
        },
        error: err => {
          this.errorMessage = 'i18n.domain.fast-checkin-session.errors.session-close-error';
          this.showError = true;
          this.spinnerService.hide();
        }});
    });
  }

  public onValidateParkingCard(parkingCardIdent: string) {
    if (this.isValidatingParkingCard) {
      return;
    }
    this.isValidatingParkingCard = true;
    this.spinnerService.show();
    this.service.validateParkingCard(this.sessionIdent!, parkingCardIdent).subscribe({next: sessionInfo => {
      this.sessionInfo = sessionInfo;
      this.showSuccess = true;
      this.spinnerService.reset();
      this.isValidatingParkingCard = false;
    },
    error: err => {
      this.errorMessage = 'i18n.domain.fast-checkin-session.errors.parking-card-validation-error';
      this.showError = true;
      this.spinnerService.reset();
      this.isValidatingParkingCard = false;
    }});
  }

  public onExtendSession(extensionInMinutes: number) {
    if (!this.cachedExtensionCalculation || this.cachedExtensionDurationMinutes !== extensionInMinutes) {
      this.errorMessage = 'i18n.domain.fast-checkin-session.errors.session-extension-price-not-available';
      this.showError = true;
      return;
    }

    if (this.cachedExtensionCalculation.needsForeignParkingPaymentProcess) {
      this.spinnerService.show();
      this.service.startExtensionPaymentProcess(this.sessionIdent!, extensionInMinutes, this.cachedExtensionCalculation).subscribe({
        next: response => {
          if (response?.success && response.redirectionUrl) {
            window.location.href = response.redirectionUrl;
          } else {
            this.spinnerService.hide();
            this.errorMessage = 'i18n.domain.fast-checkin-session.errors.session-extension-error';
            this.showError = true;
          }
        },
        error: err => {
          this.spinnerService.hide();
          this.errorMessage = 'i18n.domain.fast-checkin-session.errors.session-extension-error';
          this.showError = true;
        }
      });
      return;
    }

    this.spinnerService.show();
    this.service.extendSessionNoPayment(this.sessionIdent!, extensionInMinutes, this.cachedExtensionCalculation!).subscribe({
      next: () => {
        this.resetCachedExtensionCalculation();
        this.loadFastCheckinParkingSessionInfo();
      },
      error: err => {
        this.errorMessage = 'i18n.domain.fast-checkin-session.errors.session-extension-error';
        this.showError = true;
        this.spinnerService.reset();
      }
    });
  }

  public onExtensionDurationChanged(extensionInMinutes: number) {
    this.resetCachedExtensionCalculation();
    this.isExtensionPriceLoading = true;

    this.service.calculateExtensionPrice(this.sessionIdent!, extensionInMinutes).subscribe({
      next: calculationResult => {
        this.cachedExtensionDurationMinutes = extensionInMinutes;
        this.cachedExtensionCalculation = calculationResult;
        this.extensionPriceWithCurrency = PriceUtils.getPriceWithCurrency(calculationResult.totalPrice, calculationResult.currency);
        this.isExtensionPriceLoading = false;
      },
      error: err => {
        this.errorMessage = 'i18n.domain.fast-checkin-session.errors.session-extension-error';
        this.showError = true;
        this.isExtensionPriceLoading = false;
      }
    });
  }

  private loadFastCheckinParkingSessionInfo() {
    this.resetCachedExtensionCalculation();
    this.spinnerService.show();
    this.service.loadFastCheckinParkingSessionInfo(this.sessionIdent!).subscribe({next: sessionInfo => {
      this.sessionInfo = sessionInfo;
      this.showSuccess = true;
      this.spinnerService.reset();
    },
    error: err => {
      this.errorMessage = 'i18n.domain.fast-checkin-session.errors.session-not-found';
      this.showError = true;
      this.spinnerService.reset();
    }});
  }

  private resetCachedExtensionCalculation(): void {
    this.cachedExtensionDurationMinutes = null;
    this.cachedExtensionCalculation = null;
    this.extensionPriceWithCurrency = '';
  }
}
